import { NavLink, useLocation } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BellRing,
  BookOpen,
  Bot,
  BrainCircuit,
  CalendarDays,
  FileText,
  Gauge,
  Goal,
  Headphones,
  Link2,
  MessageCircle,
  PenTool,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  UserCog,
  Zap,
  Languages,
  Images,
} from "lucide-react";

type NavigationItem = {
  label: string;
  to: string;
  icon: typeof Gauge;
};

const navigationGroups: Array<{ label: string; items: NavigationItem[] }> = [
  {
    label: "Kontrol merkezi",
    items: [
      { label: "Genel Bakış", to: "/admin", icon: Gauge },
      { label: "Onay ve Hata Merkezi", to: "/admin/approvals", icon: BellRing },
      { label: "AI Asistan", to: "/admin/assistant", icon: BrainCircuit },
      { label: "Meta Pixel", to: "/admin/meta-pixel", icon: BarChart3 },
      { label: "Agent Kontrolü", to: "/admin/agents", icon: Bot },
      { label: "Uptime İzleme", to: "/admin/uptime", icon: Activity },
      { label: "Kullanıcılar", to: "/admin/users", icon: UserCog },
    ],
  },
  {
    label: "Klinik ve iletişim",
    items: [
      { label: "Lead Yönetimi", to: "/admin/leads", icon: Goal },
      { label: "WhatsApp", to: "/admin/whatsapp", icon: MessageCircle },
      { label: "Canlı Destek", to: "/admin/live-support", icon: Headphones },
      { label: "Calendar View", to: "/bookings-calendar", icon: CalendarDays },
      { label: "Bilgi Bankası", to: "/admin/knowledge", icon: BookOpen },
    ],
  },
  {
    label: "Büyüme ve içerik",
    items: [
      { label: "Blog Management", to: "/admin/blog", icon: FileText },
      { label: "Galeri Yönetimi", to: "/admin-gallery", icon: Images },
      
      { label: "Blog Çevirileri", to: "/admin/blog-translations", icon: Languages },
      { label: "Landing Pages", to: "/admin/landing-pages", icon: Sparkles },
      { label: "AI Blog Yazıcı", to: "/admin/blog-writer", icon: PenTool },
      { label: "Search Console", to: "/admin/search-console", icon: Search },
      { label: "Rakip Analizi", to: "/admin/competitor-analysis", icon: BarChart3 },
      { label: "Backlinkler", to: "/admin/backlinks", icon: Link2 },
      { label: "WA Otomasyon", to: "/admin/whatsapp-automation", icon: Zap },
      { label: "GTM Setup", to: "/admin/gtm-setup", icon: Settings },
    ],
  },
];

function isCurrentPath(pathname: string, target: string): boolean {
  if (target === "/admin") return pathname === target;
  return pathname === target || pathname.startsWith(`${target}/`);
}

function LinkItem({ item, mobile = false }: { item: NavigationItem; mobile?: boolean }) {
  const location = useLocation();
  const active = isCurrentPath(location.pathname, item.to);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
        mobile ? "min-w-max border px-3" : "w-full"
      } ${active
        ? "border-primary/20 bg-primary text-primary-foreground shadow-sm"
        : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-950"}`}
      aria-current={active ? "page" : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AdminSidebar() {
  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 border-r border-slate-200 bg-white lg:flex lg:flex-col">
        <div className="border-b border-slate-200 px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm"><Sparkles className="h-5 w-5" /></div>
            <div><p className="font-bold tracking-tight">Hair Art Clinics</p><p className="text-xs text-muted-foreground">Admin çalışma alanı</p></div>
          </div>
        </div>
        <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 py-5" aria-label="Admin navigasyonu">
          {navigationGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{group.label}</p>
              <div className="space-y-1">{group.items.map((item) => <LinkItem key={item.to} item={item} />)}</div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <NavLink to="/" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-950"><ShieldCheck className="h-4 w-4" /> Siteyi aç</NavLink>
        </div>
      </aside>

      <div className="border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Admin çalışma alanı</div>
        <nav className="flex gap-2 overflow-x-auto pb-1" aria-label="Mobil admin navigasyonu">
          {navigationGroups.flatMap((group) => group.items).map((item) => <LinkItem key={item.to} item={item} mobile />)}
        </nav>
      </div>
    </>
  );
}
